import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { Application, Environment } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';

/**
 * Creates an engagement, and the application it runs against if there isn't one yet.
 *
 * An engagement requires an `applicationId`, so a tenant with an empty catalogue could not
 * create one at all. Rather than send people to a separate page first, the picker offers a
 * new application inline and the two calls run in order.
 */

const ENVIRONMENTS: Environment[] = ['UAT', 'STAGING', 'PRODUCTION'];
const NEW_APPLICATION = '__new__';

export function NewEngagementDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const applications = useQuery({ queryKey: ['applications'], queryFn: api.listApplications });

  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<Environment>('UAT');
  const [applicationId, setApplicationId] = useState('');
  const [newApplicationName, setNewApplicationName] = useState('');

  const existing: Application[] = applications.data?.items ?? [];
  // Default to creating one when the catalogue is empty, so the form is never unusable.
  const choice = applicationId || (existing.length === 0 ? NEW_APPLICATION : '');
  const creatingApplication = choice === NEW_APPLICATION;

  const create = useMutation({
    mutationFn: async () => {
      const appId = creatingApplication
        ? (
            await api.createApplication({
              name: newApplicationName.trim(),
              criticality: 'MEDIUM',
            })
          ).id
        : choice;

      return api.createEngagement({ applicationId: appId, name: name.trim(), environment });
    },
    onSuccess: async (engagement) => {
      await queryClient.invalidateQueries({ queryKey: ['engagements'] });
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      onCreated(engagement.id);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };

  const error = create.error;
  const disabled =
    create.isPending ||
    name.trim().length === 0 ||
    (creatingApplication ? newApplicationName.trim().length === 0 : choice.length === 0);

  return (
    <div className="modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="card modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-eng-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="new-eng-title" className="panel-title">
          New engagement
        </h2>
        <p className="muted" style={{ fontSize: 13, margin: '0 0 4px' }}>
          Creates the engagement in <strong>DRAFT</strong>. Nothing is tested until a scope is
          defined and approved.
        </p>

        {error ? (
          <p className="login-error" role="alert">
            {error instanceof ApiError ? error.message : 'Could not create the engagement.'}
            {error instanceof ApiError && error.fieldErrors?.length ? (
              <span style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                {error.fieldErrors.map((f) => `${f.path}: ${f.message}`).join(' · ')}
              </span>
            ) : null}
          </p>
        ) : null}

        <form onSubmit={submit} className="modal-form">
          <div className="field">
            <label htmlFor="eng-name">Engagement name</label>
            <input
              id="eng-name"
              className="input"
              required
              maxLength={160}
              placeholder="Q4 external assessment"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="eng-app">Application under test</label>
            <select
              id="eng-app"
              className="input"
              value={choice}
              onChange={(e) => {
                setApplicationId(e.target.value);
              }}
              disabled={applications.isLoading}
            >
              {existing.length > 0 ? <option value="">Select an application…</option> : null}
              {existing.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
              <option value={NEW_APPLICATION}>+ Register a new application</option>
            </select>
            {applications.isError ? (
              <span className="muted" style={{ fontSize: 12 }}>
                Could not load applications — you can still register a new one.
              </span>
            ) : null}
          </div>

          {creatingApplication ? (
            <div className="field">
              <label htmlFor="app-name">New application name</label>
              <input
                id="app-name"
                className="input"
                required
                maxLength={160}
                placeholder="Client portal"
                value={newApplicationName}
                onChange={(e) => setNewApplicationName(e.target.value)}
              />
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="eng-env">Environment</label>
            <select
              id="eng-env"
              className="input"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as Environment)}
            >
              {ENVIRONMENTS.map((env) => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </select>
            {environment === 'PRODUCTION' ? (
              // Production is testable, but never by accident — §5 puts it behind scope approval.
              <span className="muted" style={{ fontSize: 12 }}>
                Production testing still requires an approved scope before anything runs.
              </span>
            ) : null}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={disabled}>
              {create.isPending ? 'Creating…' : 'Create engagement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
